/**
 * Друкована форма «Акт списання товарів» (2026-08-12, рішення власника).
 * Довільна форма: організація, № і дата, причина списання, таблиця
 * товарів зі складом ПО РЯДКАХ (0415), кількість, собівартість (для
 * проведеного — з FIFO/WAVG або клону 1С); підписи комісії та МВО.
 * Єдиний стиль друку (print-page + print-mode, кнопки друкувати/назад).
 */
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { goodsWriteoffs } from '@/api/goodsWriteoffs';
import { orgs, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import PrintActions from '@/shared/components/PrintActions';
import { partyCodesText } from '@/shared/utils/partyCodes';

const fmtQty = (n: number | string) =>
  Number(n).toLocaleString('uk-UA', { maximumFractionDigits: 3 });
const fmtMoney = (n: number | string) =>
  Number(n).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const REASON_LABELS: Record<string, string> = {
  SHORTAGE: 'нестача',
  DAMAGE: 'брак / псування',
  USAGE: 'використання на власні потреби',
};

export default function GoodsWriteoffPrintPage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  const docQ = useQuery({
    queryKey: ['goods-writeoff', id],
    queryFn: () => goodsWriteoffs.get(id!),
    enabled: !!id,
  });
  const d = docQ.data;
  const orgQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const org = orgQ.data?.items.find((o) => o.id === d?.organization_id);
  const whQ = useQuery({
    queryKey: ['warehouses', d?.organization_id],
    queryFn: () => refs.warehouses(d!.organization_id),
    enabled: !!d?.organization_id,
  });
  const nomListQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.listAll(),
  });
  const uomsQ = useQuery({ queryKey: ['uoms'], queryFn: refs.uoms });

  if (!d || !org) {
    return <div style={{ padding: '2rem' }}>Завантаження…</div>;
  }

  const whName = new Map((whQ.data ?? []).map((w) => [w.id, w.name]));
  const nomById = new Map((nomListQ.data?.items ?? []).map((n) => [n.id, n]));
  const uomById = new Map((uomsQ.data ?? []).map((u) => [u.id, u.short_name]));
  const wh = (it: { warehouse_id?: string | null }) =>
    whName.get(it.warehouse_id ?? d.warehouse_id ?? '') ?? '—';
  const totalQty = d.items.reduce((s, it) => s + Number(it.quantity), 0);
  const totalCost = d.items.reduce((s, it) => s + Number(it.amount ?? 0), 0);
  const reasonLabel = REASON_LABELS[d.writeoff_reason] ?? d.writeoff_reason;

  return (
    <div className="print-page" style={{
      maxWidth: 800, margin: '0 auto', padding: '1.5rem',
      fontFamily: "'Times New Roman', serif", fontSize: '10.5pt', background: 'var(--bg-panel)',
    }}>
      <style>{`
        .gw-table { width: 100%; border-collapse: collapse; margin: 0.5rem 0; font-size: 9pt; line-height: 1.15; }
        .gw-table th, .gw-table td { border: 1px solid #000; padding: 1px 4px; }
        .gw-table th { text-align: center; font-weight: 600; font-size: 8pt; background: #f5f5f5; text-transform: none; letter-spacing: 0; }
      `}</style>

      <PrintActions docMeta={{
        organizationId: d.organization_id,
        documentType: 'doc.goods_writeoffs',
        documentId: d.id,
        filename: `Акт списання ${d.document_number}`,
        orgName: org.name,
      }} />

      <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
        <div style={{ fontSize: '10pt' }}>{org.full_name || org.name}</div>
        {partyCodesText(org) && <div style={{ fontSize: '10pt', color: '#555' }}>{partyCodesText(org)}</div>}
      </div>

      <h2 style={{ textAlign: 'center', margin: '0.75rem 0' }}>
        Акт списання товарів № {d.document_number}
        {' '}від {new Date(d.document_date).toLocaleDateString('uk-UA')}
      </h2>
      <div style={{ textAlign: 'center', marginBottom: '0.75rem', fontSize: '10pt', color: '#555' }}>
        Причина списання: <strong>{reasonLabel}</strong>
        {d.reason_text && <> — {d.reason_text}</>}
      </div>

      <table className="gw-table">
        <thead>
          <tr>
            <th style={{ width: 30 }}>№</th>
            <th>Найменування товару</th>
            <th style={{ width: 130 }}>Склад</th>
            <th style={{ width: 70 }}>К-сть</th>
            <th style={{ width: 45 }}>Од.</th>
            {d.posted && <th style={{ width: 90 }}>Собівартість, грн</th>}
          </tr>
        </thead>
        <tbody>
          {d.items.map((it, i) => {
            const nom = nomById.get(it.nomenclature_id);
            return (
              <tr key={i}>
                <td style={{ textAlign: 'center' }}>{i + 1}</td>
                <td>{nom?.name ?? '—'}</td>
                <td>{wh(it)}</td>
                <td style={{ textAlign: 'right' }}>{fmtQty(it.quantity)}</td>
                <td style={{ textAlign: 'center' }}>
                  {nom?.base_uom_id ? (uomById.get(nom.base_uom_id) ?? 'шт') : 'шт'}
                </td>
                {d.posted && (
                  <td style={{ textAlign: 'right' }}>{fmtMoney(it.amount ?? 0)}</td>
                )}
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ fontWeight: 700 }}>
            <td colSpan={3} style={{ textAlign: 'right' }}>Разом:</td>
            <td style={{ textAlign: 'right' }}>{fmtQty(totalQty)}</td>
            <td></td>
            {d.posted && <td style={{ textAlign: 'right' }}>{fmtMoney(totalCost)}</td>}
          </tr>
        </tfoot>
      </table>

      <p style={{ margin: '0.5rem 0' }}>
        Всього найменувань {d.items.length}
        {d.posted && <>, на суму собівартості <strong>{fmtMoney(totalCost)} грн</strong></>}.
        Комісія засвідчує, що зазначені товари підлягають списанню
        ({reasonLabel}).
      </p>
      {d.comment && <p style={{ margin: '0.5rem 0', color: '#333' }}>Коментар: {d.comment}</p>}

      <table style={{ width: '100%', marginTop: 28, fontSize: '10.5pt' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <strong>Голова комісії</strong><br /><br />
              _______________________
              <br /><span style={{ color: '#333', fontSize: '9pt' }}>підпис, П.І.Б.</span>
              <br /><br />
              <strong>Члени комісії</strong><br /><br />
              _______________________<br /><br />
              _______________________
            </td>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <strong>Матеріально відповідальна особа</strong><br /><br />
              _______________________
              <br /><span style={{ color: '#333', fontSize: '9pt' }}>підпис, П.І.Б.</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
